/* eslint-disable @typescript-eslint/no-explicit-any */
import { GetStaticProps } from 'next';
import { useEffect, useState } from 'react';

import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';

import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { getApolloClient } from '@/utils/apollo-client';
import { transformMenus } from '@/utils/transformMenus';
import { withLayout } from '@/layout/Layout';

import { LayoutProps } from '@/layout/Layout/Layout.props';
import { PageProps } from '@/types/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { removeLastTrailingSlash } from '@/utils/removeLastTrailingSlash';

import { Section } from '@/components/common/Section';
import { Heading } from '@/components/typography';
import { Pagination } from '@/components/navigation/Pagination';
import { useTranslation } from 'next-i18next';
import { PRODUCTS_PAGE_QUERY } from '@/data/productsPageQuery';
import { DrugCard } from '@/components/cards/DrugCard';
import ArrowIcon from '@/public/icons/arrow-forward.svg';
import { useRouter } from 'next/router';
import { useCategoryFilter } from '@/hooks';
import { getUniqueCategories } from '@/utils/getUniqueCategories';
import { ROUTES } from '@/utils/routes';
import { Popup } from '@/components/common/Popup';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import { paginate } from '@/utils/paginate';
import { ParentIdEnum } from '@/data/therapeuticCategories';
import { PageHead } from '@/components/common/PageHead';

// LABEL: Page component
const Page = ({ data }: PageProps & LayoutProps) => {
  const router = useRouter();
  const { activeCategory, handleCategoryClick } = useCategoryFilter();
  const [isMounted, setIsMounted] = useState(false);
  const [isConfirmationStored, setIsConfirmationStored] = useState(false);
  const { t } = useTranslation('common');
  const { title, seo } = data?.pageBy?.translation ?? {};
  const productList = data?.allProducts?.nodes ?? {};
  const btnText = t('buttonMore');
  const btnConfirmText = t('confirmBtnText');
  const btnCancelText = t('cancelBtnText');
  const confirmText = t('confirmText');
  const [currentCategorySlug, setCurrentCategorySlug] = useState(
    (router.query.category as string) ?? '',
  );
  const [queryPage, setQueryPage] = useState(Number(router.query.page) || 1);

  const defaultAllowedSlugs = [
    'neurology',
    'cardiology',
    'andrology-and-gastroenterology',
    'traumatology-orthopedics-surgery',
  ];

  const allowedSlugs =
    data?.categories?.nodes.map(item => item.slug) ?? defaultAllowedSlugs;

  useEffect(() => {
    if (router.query.category) {
      setCurrentCategorySlug(router.query.category as string);
    }
    if (router.query.page) {
      setQueryPage(Number(router.query.page));
    }
  }, [router.query]);

  useEffect(() => {
    const { category, page } = router.query;

    if (category) {
      setCurrentCategorySlug(Array.isArray(category) ? category[0] : category);
    }

    if (page) {
      setQueryPage(Number(Array.isArray(page) ? page[0] : page));
    }
  }, [router.query]);

  const isActive = activeCategory === currentCategorySlug;

  const filteredProductList = isActive
    ? productList.filter((item: any) =>
        item.categories?.nodes.some(
          (category: any) => category.translation.slug === activeCategory,
        ),
      )
    : productList;

  const PAGE_SIZE = 9;
  const totalPages = Math.ceil(filteredProductList.length / PAGE_SIZE);

  const handleCategoryButtonClick = (categorySlug: string) => {
    if (categorySlug === activeCategory) {
      router.push(ROUTES.PRODUCTS).then(() => {
        handleCategoryClick('');
      });
      return;
    }

    router
      .push({
        pathname: `${ROUTES.PRODUCTS}`,
        query: { category: categorySlug, page: 1 },
      })
      .then(() => {
        handleCategoryClick(categorySlug);
      });
  };

  useEffect(() => {
    setIsMounted(true);
    if (router.query.category) {
      handleCategoryClick(router.query.category as string);
    }
  }, [handleCategoryClick, router.query.category]);

  useEffect(() => {
    const confirmation = localStorage.getItem('confirmation');
    if (confirmation === 'true') {
      setIsConfirmationStored(true);
    }
  }, []);

  return (
    <>
      <PageHead
        title={seo?.title || title}
        seo={seo || {}}
        canonicalUrl={
          Object.keys(router.query).length !== 0
            ? `/${router.asPath}`
            : seo?.canonicalUrl
        }
        breadcrumbs={seo?.breadcrumbs}
      />
      {isMounted && (
        <>
          {!isConfirmationStored && (
            <Popup
              btnConfirmText={btnConfirmText}
              btnCancelText={btnCancelText}
              onConfirm={() => setIsConfirmationStored(true)}
            >
              <p
                dangerouslySetInnerHTML={{ __html: confirmText }}
                className="!mb-10"
              />
            </Popup>
          )}
          <Section breadcrumbs={seo?.breadcrumbs} lazy={false}>
            {title && (
              <Heading
                tag="h1"
                withDivider
                content={title}
                className="mb-20 md:mb-25"
              />
            )}
            <ul className="mb-[60px] grid gap-5 md:grid-cols-2 md:gap-6 lg:max-w-[70%]">
              {getUniqueCategories(productList, allowedSlugs).map(
                (item: any) => (
                  <li key={item.translation.name}>
                    <button
                      className={`flex items-center gap-4 ${
                        isActive &&
                        currentCategorySlug === item.translation.slug &&
                        'text-accentMain'
                      }`}
                      onClick={() =>
                        handleCategoryButtonClick(item.translation.slug)
                      }
                    >
                      <ArrowIcon className="h-3 w-3 text-accentMain" />
                      <span className="text-left">{item.translation.name}</span>
                    </button>
                  </li>
                ),
              )}
            </ul>

            {filteredProductList && (
              <ul className="grid gap-8 md:grid md:grid-cols-2 lg:grid-cols-3">
                {paginate(filteredProductList, queryPage, PAGE_SIZE).map(
                  item => {
                    const {
                      title,
                      id,
                      uri,
                      featuredImage,
                      categories,
                      productCard,
                    } = item;
                    const buyAggregator = productCard?.productInfo?.aggregator;
                    const categoryData = categories?.nodes.filter(
                      (category: { translation: { slug: string } }) =>
                        allowedSlugs.includes(category.translation.slug),
                    );
                    return (
                      <li key={id} className="md:h-full">
                        <DrugCard
                          title={title}
                          buttonLabel={btnText}
                          drugImage={featuredImage?.node}
                          category={
                            categoryData && categoryData.length > 0
                              ? categoryData[0].translation
                              : undefined
                          }
                          linkUrl={removeLastTrailingSlash(uri)}
                          isInnerLink={true}
                          buyAggregator={buyAggregator}
                        />
                      </li>
                    );
                  },
                )}
              </ul>
            )}

            {totalPages > 1 && (
              <Pagination
                currentPage={queryPage}
                totalPages={totalPages}
                className="mt-15 md:mt-25"
              />
            )}
          </Section>
        </>
      )}
    </>
  );
};

export default withLayout(Page);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const client = getApolloClient();

  const location =
    locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;
  const language = locale?.toUpperCase();
  const prodLanguage = language;
  const parentId = locale == 'uk' ? ParentIdEnum.UK : ParentIdEnum.EN;

  const { data } = await client.query({
    query: PRODUCTS_PAGE_QUERY,
    variables: {
      language,
      prodLanguage,
      parentId,
    },
  });

  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });
  const footerLocation =
    locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data,
      menus,
      footerData,
      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  };
};
